import { useCallback, useContext, useEffect, useState } from "react";
import { useApiClient } from "@/context/ApiContext";
import { CurrentUserContext } from "@/context/CurrentUserContext";
import { getClientsWithFilters } from "@/services/client/Client";
import {
  createProcess,
  getMatchedClientsForStaff,
  getPostingMatchForStaff,
} from "@/services/staffing/Staffing";
import { Client } from "@/types/common/client";
import { MatchedClient } from "@/types/staffing/matchedClient";
import { PostingStaffMatch } from "@/types/staffing/postingStaffMatch";

/** How many matches the embedded card list pulls. */
export const OPENINGS_TOP = 20;

/**
 * Why openings cannot be matched, when they can't:
 * - "session": the signed in user never resolved, i.e. the `me` call failed
 * - "region": the user resolved but has no region to match against
 */
export type OpeningsBlocker = "session" | "region" | null;

export interface UseOpeningsResult {
  openings: MatchedClient[];
  loading: boolean;
  error: string | null;
  blocker: OpeningsBlocker;
  refresh: () => void;
  expressInterest: (opening: MatchedClient) => Promise<void>;
}

/** Shared by the list and detail hooks; see OpeningsBlocker. */
const resolveBlocker = (
  userLoading: boolean,
  currentUser: { Id: number; LoginName?: string; Region?: string },
): OpeningsBlocker => {
  if (userLoading) return null;
  // Id stays 0 until `me` resolves, which separates a failed session from a thin profile
  if (currentUser.Id === 0) return "session";
  if (!currentUser.LoginName || !currentUser.Region) return "region";
  return null;
};

/**
 * The "express interest" mutation, shared by the list and detail hooks.
 * `refresh` re-fetches whichever view fired it so the new process shows up.
 */
const useExpressInterest = (refresh: () => void) => {
  const apiClient = useApiClient();
  const { currentUser, originalUser } = useContext(CurrentUserContext);
  const { LoginName, Region, FullName, Role } = currentUser;

  return useCallback(
    async (opening: MatchedClient) => {
      if (opening.postingID == null) {
        throw new Error("This opening no longer has an active posting.");
      }
      await createProcess(
        {
          postingId: opening.postingID,
          staff: `positive\\${LoginName}`,
          source: "Openings",
          applicant: false,
          author: originalUser.LoginName,
          adHocGroup: {
            clientId: opening.clientID,
            clientName: opening.clientName,
            staffFullName: FullName ?? "",
            staffRole: Role ?? "",
            staffRegion: Region ?? "",
          },
        },
        apiClient,
      );
      refresh();
    },
    [
      LoginName,
      originalUser.LoginName,
      FullName,
      Role,
      Region,
      apiClient,
      refresh,
    ],
  );
};

/**
 * Mobile/embedded counterpart of the data fetching done by ClientMatchesList
 * for the Openings widget: matched clients for the signed in staff member,
 * plus the "express interest" mutation.
 */
export const useOpenings = (): UseOpeningsResult => {
  const apiClient = useApiClient();
  const { currentUser, isLoading: userLoading } = useContext(CurrentUserContext);
  const [openings, setOpenings] = useState<MatchedClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { LoginName, Region } = currentUser;

  const refresh = useCallback(() => {
    if (!LoginName || !Region) return;
    setLoading(true);
    setError(null);
    getMatchedClientsForStaff(
      "positive\\" + LoginName,
      Region,
      OPENINGS_TOP,
      apiClient,
    )
      .then((res) => setOpenings(res.data ?? []))
      .catch((err) => {
        console.error(err);
        setError(
          "Unable to load your openings. Pull down or tap retry to try again.",
        );
      })
      .finally(() => setLoading(false));
  }, [LoginName, Region, apiClient]);

  useEffect(() => {
    if (userLoading) return;
    if (!LoginName || !Region) {
      setLoading(false);
      return;
    }
    refresh();
  }, [userLoading, LoginName, Region, refresh]);

  return {
    openings,
    loading: loading || userLoading,
    error,
    blocker: resolveBlocker(userLoading, currentUser),
    refresh,
    expressInterest: useExpressInterest(refresh),
  };
};

export interface UseOpeningDetailResult {
  opening: MatchedClient | null;
  loading: boolean;
  error: string | null;
  blocker: OpeningsBlocker;
  refresh: () => void;
  expressInterest: (opening: MatchedClient) => Promise<void>;
}

/**
 * The posting match endpoint returns the staff side of the match plus the
 * clientId; the client fields the opening UI renders come from the clients
 * endpoint. Folds both into the MatchedClient shape the list already uses.
 */
const toOpening = (
  match: PostingStaffMatch,
  client: Client | null,
): MatchedClient => ({
  clientID: match.clientId,
  postingID: match.postingId,
  isTelehealth: client?.TelehealthOption ?? false,
  clientName: client?.NameFL ?? client?.Name ?? "",
  age: client?.Age ?? null,
  gender: client?.Gender ?? null,
  clientStatus: client?.ClientStatus ?? "",
  clientLang: client?.Language ?? "",
  parentCaregiver1: client?.ParentCaregiver1Name ?? "",
  parentCaregiver1City: client?.ParentCaregiver1City ?? null,
  parentCaregiver1County: client?.ParentCaregiver1County ?? "",
  insuranceCompany: client?.InsuranceCompany ?? "",
  enrollmentStatus: client?.EnrollmentStatus ?? "",
  fundingSourceTypes: client?.FundingSourceTypes ?? "",
  distance: match.distance,
  percentMatchAvail: match.percentMatchAvail,
  percentMatchDistance: match.percentMatchDistance,
  percentMatchCredentials: match.percentMatchCredentials,
  percentMatch: match.percentMatch,
  processRowID: match.processRowId,
});

/**
 * Data for the single opening view, in two steps: the match between one
 * posting and the signed in staff member from
 * staffing/matches/postings/{postingId}/staff, then the client record it
 * points at (the match only carries the clientId). A 404 resolves to
 * `opening: null` (staffed, closed, or never a match) rather than an error.
 *
 * `loginNameOverride` fetches the match for someone other than the signed in
 * user (full form, e.g. "positive\\calnolan"); express interest still runs as
 * the signed in user.
 */
export const useOpeningDetail = (
  postingId: number | null,
  loginNameOverride?: string,
): UseOpeningDetailResult => {
  const apiClient = useApiClient();
  const { currentUser, isLoading: userLoading } = useContext(CurrentUserContext);
  const [opening, setOpening] = useState<MatchedClient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { LoginName, Region } = currentUser;
  const matchLogin =
    loginNameOverride ?? (LoginName ? "positive\\" + LoginName : "");

  const refresh = useCallback(() => {
    if (postingId == null || !matchLogin || !Region) return;
    setLoading(true);
    setError(null);
    getPostingMatchForStaff(postingId, matchLogin, apiClient)
      .then(async (res) => {
        const match: PostingStaffMatch | null = res.data ?? null;
        if (!match) {
          setOpening(null);
          return;
        }
        const clientRes = await getClientsWithFilters(
          { filter: `Id eq ${match.clientId}` },
          apiClient,
        );
        const client: Client | null =
          clientRes.data.value?.[0] ?? clientRes.data?.[0] ?? null;
        setOpening(toOpening(match, client));
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setOpening(null);
          return;
        }
        console.error(err);
        setError("Unable to load this opening. Tap retry to try again.");
      })
      .finally(() => setLoading(false));
  }, [postingId, matchLogin, Region, apiClient]);

  useEffect(() => {
    if (userLoading) return;
    if (postingId == null || !matchLogin || !Region) {
      setLoading(false);
      return;
    }
    refresh();
  }, [userLoading, postingId, matchLogin, Region, refresh]);

  return {
    opening,
    loading: loading || userLoading,
    error,
    blocker: resolveBlocker(userLoading, currentUser),
    refresh,
    expressInterest: useExpressInterest(refresh),
  };
};

/** Turns a failed expressInterest call into something worth showing on a phone. */
export const getInterestErrorMessage = (err: any): string => {
  if (err?.response?.status === 409) {
    return (
      err.response.data?.detail ??
      "You have already expressed interest in this posting."
    );
  }
  return (
    err?.response?.data?.detail ??
    err?.message ??
    "Something went wrong. Please try again."
  );
};
